import type { DateAttendances } from '@mathflat/domain/Attendance'
import { makeAutoObservable } from 'mobx'
import { computedFn } from 'mobx-utils'
import type { DateStr } from 'src/types'

import type { Student } from '..'
import { Day } from '../common/Calendar/Day'
import { Attendance } from './Attendance'
import { AttendanceByDay } from './AttendanceByDay'

type Depth2Key = {
  studentId: Student['id']
  day: Day
}
type Depth3Key = Depth2Key & {
  attId: Attendance['id']
}

type AttendanceFromStudentId = Map<AttendanceByDay['dateKey'], AttendanceByDay>

// TODO: 나중에 백엔드에게 요청할 데이터 구조를 학생 ID를 통한 요청으로 변경하는게 더 편할 것 같네요.
/***
 * 3Depth: 학생Id -> yyyy-MM-dd -> (AttendanceId -> Attendance) ()가 AttendanceByDay
 * 학생Id로 조회하는 경우가 제일 많기 때문에...
 *
 * 데이터 구조는 아래와 같습니다.
 * ex)
 *
 * Map {                                      // 0 Depth
 *  'I1': {                                   // 1 Depth
 *    '2021-08-05' AttendanceByDay::Map {     // 2 Depth
 *      84723891: Attendance {                // 3 Depth
 *        enterDatetime: ...
 *        quitDatetime: ...
 *      },
 *      84723892: Attendance {
 *        enterDatetime: ...
 *        quitDatetime: ...
 *      }
 *    },
 *    '2021-08-06' Map {
 *      ...
 *    }
 *  }
 *  'I2': {
 *    ...
 *  }
 * }
 ***/
export class AttendanceByStudentId {
  // 1 Depth
  private attByStudentId = new Map<Student['id'], AttendanceFromStudentId>()

  constructor(data: DateAttendances[]) {
    makeAutoObservable(this)
    this.updateFromAttList(data)
  }

  // 학생 ID별 출퇴실 횟수 계산
  attCountByStudentId = computedFn((studentId: Student['id']) => {
    const attByStudent = this.attByStudentId.get(studentId)
    if (!attByStudent) return

    let enter = 0
    let quit = 0
    for (const attByDay of attByStudent.values()) {
      enter += attByDay.attCount.enter
      quit += attByDay.attCount.quit
    }

    return { enter, quit }
  })

  // 해당 일의 모든 출결 수
  allAttendanceByDate = computedFn(
    (studentIdList = [...this.attByStudentId.keys()], dateKey: `${number}-${number}-${number}`) => {
      let enter = 0
      let quit = 0

      studentIdList.forEach((studentId) => {
        const result = this.allAttendanceByDateStudentId(studentId, dateKey)
        enter += result.enter
        quit += result.quit
      })
      return {
        enter,
        quit,
      }
    },
  )

  // 학생 한명의 특정 날의 출결 정보
  allAttendanceByDateStudentId = computedFn((studentId: Student['id'], dateKey: DateStr<'-'>) => {
    const attByStudent = this.attByStudentId.get(studentId)

    let enter = 0
    let quit = 0
    if (!attByStudent)
      return {
        enter,
        quit,
      }

    for (const attByDay of attByStudent.values()) {
      if (attByDay.dateKey === dateKey) {
        attByDay.toArr.forEach((att) => {
          if (att.enterDatetime) enter += 1
          if (att.quitDatetime) quit += 1
        })
      }
    }
    return {
      enter,
      quit,
    }
  })

  get allAttendanceInfo() {
    const studentIdList = [...this.attByStudentId.keys()]
    let enter = 0
    let quit = 0

    studentIdList.forEach((studentId) => {
      const result = this.attCountByStudentId(studentId)
      if (result) {
        enter += result.enter
        quit += result.quit
      }
    })
    return {
      enter,
      quit,
    }
  }

  attendanceByDate(studentIdList: Student['id'][]) {
    const dateSet = new Set<`${number}-${number}-${number}`>()

    studentIdList.forEach((studentId) => {
      const target = this.attByStudentId.get(studentId)
      if (target) {
        const attednanceDayList = [...target.values()]
        attednanceDayList.forEach((date) => {
          dateSet.add(date.dateKey)
        })
      }
    })

    return dateSet
  }

  // 여러 raw 데이터를 Attendance들로 변환 후 추가함
  updateFromAttList(data: DateAttendances[]) {
    for (const dateAttendanceList of data) {
      this.addStudentDayAtt(dateAttendanceList)
    }
  }

  // 0 Depth
  get() {
    return this.attByStudentId
  }

  // 1 Depth Set
  set(studentId: Student['id'], attendanceFromStudentId: AttendanceFromStudentId) {
    this.attByStudentId.set(studentId, attendanceFromStudentId)
  }

  // 1 Depth Delete
  delete(studentId: Student['id']) {
    this.attByStudentId.delete(studentId)
  }

  // 1 Depth
  getAttFromStudentId(studentId: Student['id']) {
    return this.attByStudentId.get(studentId)
  }

  // 2 Depth
  getDayAtt({ day, studentId }: Depth2Key) {
    return this.getAttFromStudentId(studentId)?.get(day.toString)
  }

  // 3 Depth
  getAtt({ attId, ...depth2key }: Depth3Key) {
    return this.getDayAtt(depth2key)?.get(attId)
  }

  // 3 Depth Delete
  deleteAtt(att: Attendance) {
    const day = Day.fromDate(att.enterDatetime)
    this.getDayAtt({ day, studentId: att.studentId })?.delete(att.id)
  }

  // 3 Depth AttList Add
  addAttList = (attList: Attendance[]) => {
    for (const att of attList) {
      this.addAtt(att)
    }
  }

  // TODO: 이 반복적인 과정을 좀 더 쉽게 할 수 없을까...??
  // 3 Depth Att Add
  addAtt(att: Attendance) {
    const day = Day.fromDate(att.enterDatetime)
    // 1Depth
    // get|set 학생 Id -> yyyy-MM-dd
    if (!this.attByStudentId.has(att.studentId)) {
      this.attByStudentId.set(att.studentId, new Map())
    }
    const byDay = this.attByStudentId.get(att.studentId)!

    // 2) get|set yyyy-MM-dd -> attendanceId
    if (!byDay.has(day.toString)) {
      byDay.set(day.toString, new AttendanceByDay(day))
    }
    const attColl = byDay.get(day.toString)!

    // 3) set attendanceId -> attendance
    attColl.set(att.id, att)
  }

  // 출퇴원을 할시 서버에서 response를 떨궈주는데 그건 서버데이터
  // 하나의 raw 데이터를 Attendance로 변환 후 추가함
  private addStudentDayAtt({ attendanceList }: DateAttendances) {
    for (const attData of attendanceList) {
      const att = new Attendance(attData)
      this.addAtt(att)
    }
  }
}
